<template>
  <OpenDemoAccountTemplate>
    <RegisterForm
      :title="$t('register.accountConfig.pageTitle')"
      :desc="$t('verificationStatus.openAccount.accountConfiguration.desc')"
      :nextButtonOnClick="submitClick"
      :nextButtonLabel="$t('common.button.submit')"
    >
      <el-form
        :model="form"
        :rules="rules"
        ref="ruleForm"
        class="mt-3 pb-5 md:pb-7 flex flex-col items-stretch justify-start"
      >
        <el-form-item prop="tradingPlatform" class="mb-6" :inline-message="true">
          <el-radio-group class="w-full" v-model="form.tradingPlatform" @change="handlePlatformChange">
            <RegisterCollapseRow
              required
              :title="$t('register.accountConfig.chooseTradingPlatform')"
              :infoText="$t(`register.accountConfig.chooseTradingPlatformInfoText`)"
              gap="gap-5"
            >
              <PlatformRadioButton
                v-for="platform in tradingPlatformOptions"
                class="min-w-56"
                :key="platform.key"
                :currValue="form.tradingPlatform"
                :value="platform.value"
              />
            </RegisterCollapseRow>
          </el-radio-group>
        </el-form-item>
        <el-form-item prop="accountType" v-if="form.tradingPlatform == 'mt4'">
          <el-radio-group class="w-full" v-model="form.accountType">
            <RegisterCollapseRow required :title="$t('openAdditAcc.chooseAccType')" gap="gap-5">
              <RegisterCheckButton
                v-for="item in $config.openLiveAccount.mt4DemoChooseType(regulator)"
                @click="form.accountType = item.value"
                :key="item.value"
                :currValue="form.accountType"
                :value="item.value"
                :data-testid="item.value"
              >
                <img class="h-full w-full object-scale-down" :src="item.img" />
              </RegisterCheckButton>
            </RegisterCollapseRow>
          </el-radio-group>
        </el-form-item>
        <el-form-item prop="accountType" v-if="form.tradingPlatform == 'mt5'">
          <el-radio-group class="w-full" v-model="form.accountType">
            <RegisterCollapseRow required :title="$t('openAdditAcc.chooseAccType')" gap="gap-5">
              <RegisterCheckButton
                v-for="item in $config.openLiveAccount.mt5DemoChooseType(regulator)"
                @click="form.accountType = item.value"
                :key="item.value"
                :currValue="form.accountType"
                :value="item.value"
                :data-testid="item.value"
              >
                <img class="h-full w-full object-scale-down" :src="item.img" />
              </RegisterCheckButton>
            </RegisterCollapseRow>
          </el-radio-group>
        </el-form-item>
        <el-form-item prop="currency" class="mb-8" :inline-message="true">
          <el-radio-group class="w-full" v-model="form.currency">
            <RegisterCollapseRow required :title="$t('openAdditAcc.chooseCurrency')" maxHeightTransition="max-h-14"
              ><RegisterCheckButton
                v-for="item in $config.openLiveAccount.chooseCurrency(regulator)"
                :key="item.currency"
                :currValue="form.currency"
                :value="item.currency"
                :data-testid="item.currency"
                cssClass="p-4 text-sm font-bold min-w-35"
              >
                <img class="h-5 w-5 object-scale-down" :src="item.img" />
                <div>{{ $t(item.symbolLabel) }}</div></RegisterCheckButton
              ></RegisterCollapseRow
            ></el-radio-group
          ></el-form-item
        >
        <el-form-item prop="leverage">
          <el-radio-group v-model="form.leverage">
            <RegisterCollapseRow required :title="$t('openAdditAcc.chooseLeverage')" maxHeightTransition="max-h-14"
              ><el-radio
                v-for="item in $config.openLiveAccount.chooseLeverage(regulator)"
                :key="item"
                :label="item"
                class="demo_radio"
                :data-testid="item"
                >{{ item }}: 1</el-radio
              ></RegisterCollapseRow
            >
          </el-radio-group>
        </el-form-item>

        <el-form-item prop="balance">
          <el-radio-group v-model="form.balance">
            <RegisterCollapseRow required :title="$t('openAdditAcc.chooseBalance')" maxHeightTransition="max-h-14"
              ><el-radio
                v-for="item in $config.openLiveAccount.chooseBalance(regulator)"
                :key="item.value"
                :label="item.value"
                class="demo_radio"
                :data-testid="item.value"
                >{{ item.name }}</el-radio
              ></RegisterCollapseRow
            >
          </el-radio-group>
        </el-form-item>
        <SelectMt4Tips v-model="mt4TipVisible" @onOk="handlePlatformChange" />
      </el-form>
    </RegisterForm>
  </OpenDemoAccountTemplate>
</template>

<script>
import { apiCreateDemoAccount } from '@/resource';
import RegisterCollapseRow from '@/components/register/RegisterCollapseRow.vue';
import RegisterCheckButton from '@/components/register/RegisterCheckButton.vue';
import PlatformRadioButton from '@/components/register/openAccount/accountConfiguration/PlatformRadioButton.vue';
import SelectMt4Tips from '@/components/register/openAccount/accountConfiguration/SelectMt4Tips.vue';
import RegisterForm from '@/components/register/registerForms/RegisterForm.vue';
import OpenDemoAccountTemplate from './OpenDemoAccountTemplate.vue';

export default {
  name: 'OpenDemoAccount',
  components: {
    RegisterCollapseRow,
    RegisterCheckButton,
    PlatformRadioButton,
    OpenDemoAccountTemplate,
    RegisterForm,
    SelectMt4Tips
  },
  data() {
    return {
      form: {
        tradingPlatform: 'mt5',
        accountType: null,
        currency: null,
        leverage: null,
        balance: null
      },
      rules: {
        accountType: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('openAdditAcc.accTypeErr')
          }
        ],
        currency: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('openAdditAcc.currError')
          }
        ],
        leverage: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('openAdditAcc.leverageErr')
          }
        ],
        balance: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('openAdditAcc.balanceErr')
          }
        ]
      },
      mt4TipVisible: false
    };
  },
  computed: {
    tradingPlatformOptions() {
      return this.$config.openLiveAccount.choosePlatform(this.regulator);
    },
    tradingPlatformParamValue() {
      switch (this.form.tradingPlatform) {
        case 'mt4':
          return 4;
        case 'mt5':
          return 5;
        default:
          return null;
      }
    }
  },
  methods: {
    handlePlatformChange(value) {
      this.form.tradingPlatform = value;
      if (value === 'mt4') {
        this.mt4TipVisible = true;
      }
    },
    createDemoAccount() {
      return apiCreateDemoAccount({ ...this.form });
    },
    submitClick() {
      return this.$refs['ruleForm'].validate(valid => {
        if (valid) {
          this.createDemoAccount().then(resp => {
            if (resp.data.code === 0) {
              this.$router.push(
                `/openDemoAccount/success${
                  this.tradingPlatformParamValue ? `?p=${this.tradingPlatformParamValue}` : ''
                }`
              );
            }
          });
        }
      });
    }
  }
};
</script>
